import { useCallback, useState } from 'react';

/** Props for adding optional checkbox selection to any table */
export interface TableSelectionProps<T> {
  selectedIds: Set<number>;
  onToggle: (item: T) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
  getId: (item: T) => number;
}

/**
 * Generic table selection hook.
 * Manages a Set<number> of selected item IDs with toggle, toggleAll, and clear.
 * One instance per table — each table manages its own selection independently.
 *
 * @param getId - Extracts the numeric ID from an item (e.g., `message => message.chatId`)
 */
export function useTableSelection<T>(getId: (item: T) => number) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const toggle = useCallback(
    (item: T) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        const id = getId(item);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [getId]
  );

  const toggleAll = useCallback(
    (items: T[]) => {
      setSelectedIds((prev) => {
        const allIds = items.map(getId);
        const allSelected =
          allIds.length > 0 && allIds.every((id) => prev.has(id));
        return allSelected ? new Set() : new Set(allIds);
      });
    },
    [getId]
  );

  const clear = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectedCount = selectedIds.size;

  const getSelectionProps = useCallback(
    (items: T[]): TableSelectionProps<T> => {
      const allIds = items.map(getId);
      const allSelected =
        allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
      const someSelected =
        !allSelected && allIds.some((id) => selectedIds.has(id));

      return {
        selectedIds,
        onToggle: toggle,
        onToggleAll: () => toggleAll(items),
        allSelected,
        someSelected,
        getId,
      };
    },
    [selectedIds, toggle, toggleAll, getId]
  );

  return {
    selectedIds,
    selectedCount,
    toggle,
    toggleAll,
    clear,
    getSelectionProps,
  };
}
